import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useReleaseNotes } from "lib/releaseNoteQueries";
import ReleaseNoteItem from "./ReleaseNoteItem";
import "./ReleaseNotes.scss";

const ReleaseNotesPage: FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useReleaseNotes();
  const notes = data ?? [];

  // Unlike the Overview card, this page is a destination — an empty screen would
  // read as a broken link, so every state says something.
  let body: ReactNode;
  if (isLoading) {
    body = (
      <p className="release-notes-page__state" data-testid="release-notes-loading">
        {t("common.loading")}
      </p>
    );
  } else if (isError) {
    body = (
      <p className="release-notes-page__state">{t("releaseNotesLoadError")}</p>
    );
  } else if (notes.length === 0) {
    body = <p className="release-notes-page__state">{t("releaseNotesEmpty")}</p>;
  } else {
    body = (
      <div className="release-notes-page__layout">
        <aside className="release-notes-page__index">
          <nav aria-label={t("releaseNotes")}>
            <ul>
              {notes.map((note) => (
                <li key={note.id}>
                  <a href={`#release-v${note.version}`}>
                    <span className="release-notes-page__index-ver">
                      v{note.version}
                    </span>
                    {note.date && (
                      <span className="release-notes-page__index-date">
                        {format(parseISO(note.date), "d MMM yyyy")}
                      </span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="release-notes-page__list">
          {notes.map((note) => (
            <ReleaseNoteItem key={note.id} note={note} variant="full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container release-notes-page">
      <Link to="/" className="release-notes-page__back" data-testid="back-to-home">
        <span aria-hidden="true">←</span> {t("backToHome")}
      </Link>

      <h1>{t("releaseNotes")}</h1>

      {body}
    </div>
  );
};

export default ReleaseNotesPage;
